<template>
  <div
    :class="[
      {
        'owc-text-media-module--text-only-offset': shouldAddOffsetToText,
        'owc-text-media-module--dark': isDarkModeOn,
        'owc-wb-accordion-content': isPartOfAccordion,
      },
      'owc-text-media-module',
    ]"
    :data-theme="brand"
  >
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <div class="owc-text-media-module__container wb-grid-container">
      <div
        class="owc-text-media-module__row wb-grid-row"
        :class="[
          {
            'owc-text-media-module__row--inverted': isTextOnLeft,
          },
          'owc-text-media-module__row',
          'wb-grid-row',
        ]"
      >
        <!-- Text Block goes here-->
        <div
          :class="[
            {
              'wb-grid-col-offset-mq3-1': !isTextOnLeft && isMediaContainer,
            },
            // Full width when there is no media block
            isMediaContainer ? 'wb-grid-col-mq3-5' : 'wb-grid-col-mq3-12',
            'owc-text-media-module__text-block',
            'wb-grid-col-mq1-12',
          ]"
        >
          <TextBlock
            v-if="personaliseableData.textBlock"
            :textBlockData="textBlock"
            :hasHeadline="hasHeadline"
            :containsMedia="isMediaContainer"
            :darkMode="isDarkModeOn"
          ></TextBlock>
          <Cta
            class="owc-text-media-module__cta-button"
            v-if="isCTAVisible"
            :ctaData="buttonData"
            :darkMode="isDarkModeOn"
          ></Cta>
        </div>

        <!-- Media Block goes here-->
        <div
          v-if="isMediaContainer"
          :class="[
            {
              'wb-grid-col-offset-mq3-1': isTextOnLeft,
            },
            'wb-grid-col-mq1-12',
            'wb-grid-col-mq3-6',
          ]"
        >
          <div
            :class="[
              {
                'owc-text-media-module__media--inverted': isTextOnLeft,
                'owc-text-media-module__author': isEditMode,
              },
              'owc-text-media-module__media',
            ]"
          >
            <div class="owc-text-media-module__media-content">
              <SlotContainer
                v-if="isEditMode"
                refName="slot-textMediaModuleItem"
                class="owc-text-media-module__slider"
                :theme="theme"
              >
                <slot></slot>
              </SlotContainer>
              <!--ATTENTION: This implementation will no longer work if workbench is updated to version 7
                since they replaced the class wb-slider__item with an actual web component. We tried ato make it
                work for that version as well but we're pretty sure that to make this a full FE solution we would need
                functional components or a similar complex solution since the items that come from the BE via slot, need
                to dynamically be wrapped in a wb-slider-item component.
              -->
              <template v-else>
                <wb-slider show-dot-navigation :theme="theme">
                  <SlotContainer :is="'template'" ref="wbSlider" refName="slot-textMediaModuleItem">
                    <slot></slot>
                  </SlotContainer>
                </wb-slider>
              </template>
            </div>
          </div>
        </div>
        <!-- CTA mobile -->
        <div class="wb-grid-col-mq1-12">
          <Cta
            class="owc-text-media-module__cta-button owc-text-media-module__cta-button--mobile"
            v-if="isCTAVisible"
            :ctaData="buttonData"
            :darkMode="isDarkModeOn"
          ></Cta>
        </div>
      </div>
    </div>
    <div v-if="footnotes" class="wb-grid-container owc-text-media-module__footnotes">
      <globalelem-footnote-component :module-id="moduleId" :theme="theme"></globalelem-footnote-component>
    </div>
  </div>
</template>

<script lang="ts">
import { TextMediaComponent } from './TextMediaModule';
import Cta from './Cta.vue';
import TextBlock from './TextBlock.vue';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import { LayoutOrientation } from './TextMediaModule.enums';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import owcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import { setUpWbSlider } from '@owc-utils/Workbench/slider-utils';
import Personalisation from '@owc-utils/Mixins/Personalisation';
import { getLocalFootnotes, setModuleId } from '@owc-utils/Footnotes/FootnotesHelper';
import { setBrandTheme } from '@owc-utils/Theming/theming';

/**
 * TextMediaModule Component for OneWeb
 * The Text Media module is a combined module of media, text content and call-to-action button.
 * Inside the module, the author could choose 3 types of content to fill:
 *  - Text (Subtitle, Headline and description)
 *  - Media (Image, Video or Slider)
 *  - Button
 * The behavior of the module needs to follow the content.
 * That means the author fill all fields the content need and the module automatically change in the website output.
 * **/

const sliderClasses = ['wb-slider__item', 'wb-slider__item--full-width'];

export default (ComponentInitializeDispatcher as TextMediaComponent).extend({
  name: 'owc-text-media-module',
  mixins: [owcDataComponent, Personalisation],
  components: {
    Cta,
    TextBlock,
    SlotContainer,
    ValidationBoxWrapper,
  },
  data() {
    return {
      sliderInitialized: false,
    };
  },
  computed: {
    buttonData: function() {
      return this.personaliseableData?.buttonData;
    },
    isPartOfAccordion: function() {
      return Boolean(this.personaliseableData?.isPartOfAccordion);
    },
    textBlock: function() {
      return this.personaliseableData?.textBlock;
    },
    isCTAVisible: function() {
      return Boolean(this.personaliseableData?.buttonData);
    },
    isTextOnLeft: function() {
      return this.personaliseableData?.layoutOrientation === LayoutOrientation.left;
    },
    isMediaContainer: function() {
      return Boolean(this.personaliseableData?.isMediaContainer);
    },
    shouldAddOffsetToText: function() {
      return !this.isMediaContainer && this.hasHeadline;
    },
    hasHeadline: function() {
      return Boolean(this.personaliseableData?.textBlock?.headline);
    },
    isDarkModeOn: function() {
      return Boolean(this.personaliseableData?.darkMode);
    },
    theme: function() {
      return this.isDarkModeOn ? 'dark' : 'light';
    },
    footnotes() {
      return getLocalFootnotes(this.personaliseableData);
    },
    moduleId() {
      return setModuleId(this);
    },
    brand() {
      return setBrandTheme();
    },
  },
  watch: {
    personaliseableData(data) {
      if (data) {
        const slider = this.$refs.wbSlider as HTMLTemplateElement;

        if (slider && !this.sliderInitialized) {
          setUpWbSlider(slider, sliderClasses);

          this.sliderInitialized = true;
        }
      }
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/aspect-ratio';
@import '@owc-styles/utils/rte';
@import '@owc-styles/utils/theming';

$moduleName: 'owc-text-media-module';
$STICK_TO_VIEWPORT_PERCENTAGE: 16.66%;

.#{$moduleName} {
  padding: var(--wb-spacing-s) 0;

  background-color: var(--wb-white);

  .#{$moduleName} {
    &__container {
      margin: 0 auto;
    }
    &__row {
      display: flex;
      flex-flow: row-reverse wrap;
      align-items: flex-start;

      @include breakpoint-from(mq3) {
        &--inverted {
          flex-direction: row;
        }
      }
    }
    &__text-block {
      align-self: center;
    }

    // CTAs are in different positions in mobile & desktop
    &__cta-button {
      display: none;
      margin-top: var(--wb-spacing-s);

      &--mobile {
        display: inline-flex;
      }

      @include breakpoint-from(mq3) {
        display: inline-flex;

        &--mobile {
          display: none;
        }
      }
    }
    &__media {
      @include aspect-ratio(3, 4);
      position: relative;

      margin-top: var(--wb-spacing-m);
      margin-bottom: var(--wb-spacing-m);

      @include breakpoint-from(mq3) {
        @include aspect-ratio(1, 1);
        margin-top: 0;
        margin-bottom: 0;
      }

      // Magic numbers in the following rules
      // This is caused by a very specific design specification to stick the media content to the viewport sides

      @include breakpoint-between(mq3, mq6) {
        left: -$STICK_TO_VIEWPORT_PERCENTAGE;

        width: 100% + $STICK_TO_VIEWPORT_PERCENTAGE;

        &--inverted {
          left: 0;
        }
      }

      &:has(wb-dot-navigation) {
        @include breakpoint-from(mq3) {
          margin-bottom: var(--wb-spacing-m);
        }
      }
    }
    &__footnotes {
      margin-top: var(--wb-spacing-s);
    }
  }
  &--text-only-offset {
    padding-bottom: var(--wb-spacing-s);
  }
  &--dark {
    background-color: var(--wb-black);
  }
  &.owc-wb-accordion-content {
    background-color: unset;

    & > .owc-text-media-module__container {
      width: 100%;
      margin: 0;

      .owc-text-media-module__media {
        @include breakpoint-between(mq3, mq6) {
          left: 0;

          width: 100%;
        }
      }
    }

    @include breakpoint-to(mq2) {
      padding: var(--wb-spacing-xxs) 0;
    }
  }
  .wb-slider__item {
    width: 100%;
    height: 100%;
  }
  wb-slider {
    visibility: hidden;

    &.hydrated {
      visibility: visible;
    }
  }
}
</style>

<!--AUTHOR CSS -->
<style lang="scss">
/**Most of these are here to override the aspect ratio mixin */
.owc-text-media-module .owc-text-media-module__author {
  .owc-text-media-module__media-content {
    position: relative;
  }
  .owc-text-media-module__slider {
    display: flex;
    flex-wrap: wrap;

    row-gap: 40px;

    ::slotted(*) {
      max-width: 50%;
    }
  }
  &:before {
    padding-top: 0;
  }
}
</style>
